import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FadeIn from 'react-fade-in';
import Page from 'src/components/Page';
import Breadcrumb from 'src/components/Breadcrumb';
import Loading from 'src/components/Loading';
import NotFoundRecords from 'src/components/NotFoundRecords';
import {
  loadSantanderPaymentReportDetails,
  LOAD_SANTANDER_PAYMENT_REPORT_DETAILS
} from 'src/store/actions/santanderClabeActions';
import {
  selectSantanderPaymentDetails,
  selectSantanderPaymentDetailsCount
} from 'src/store/selectors/santanderClabeSelectors';
import {
  isLoadingSelector,
  successSelector
} from 'src/store/selectors/statusSelector';
import selectFormatDate from 'src/utils/selectDateFormat';
import dateFormatType from 'src/utils/dateFormatType';
import SantanderPaymentDetailsTable from './SantanderPaymentDetailsTable';
import useStyles from './styles';

const SantanderReportDetail = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reportId } = useParams();
  const location = useLocation();

  const paymentDetails = useSelector(selectSantanderPaymentDetails);
  const paymentDetailsCount = useSelector(selectSantanderPaymentDetailsCount);
  const loading = useSelector(
    isLoadingSelector([LOAD_SANTANDER_PAYMENT_REPORT_DETAILS.action])
  );
  const success = useSelector(
    successSelector([LOAD_SANTANDER_PAYMENT_REPORT_DETAILS.action])
  );

  const handleChangePage = useCallback(
    (event, newPage) => {
      dispatch(
        loadSantanderPaymentReportDetails({
          santanderReportId: reportId,
          options: { page: newPage }
        })
      );
    },
    [dispatch, reportId]
  );

  useEffect(() => {
    if (reportId) {
      dispatch(
        loadSantanderPaymentReportDetails({ santanderReportId: reportId })
      );
    }
  }, [dispatch, reportId]);

  const reportDateFromList = location?.state?.reportDate;
  const paymentDetailDate = !loading && paymentDetails?.[0]?.date;
  const reportDate = reportDateFromList || paymentDetailDate;
  const reportDateLabel = reportDate
    ? selectFormatDate(reportDate, dateFormatType.DAY)
    : null;


  return (
    <Page
      className={classes.ContainerRoot}
      title={t('SANTANDER_REPORTS.DAILY_REPORT_DETAILS')}
    >
      <FadeIn transitionDuration={200}>
        <Container maxWidth={false}>
          <Breadcrumb doNotRedirectIndexList={[2]} />
          <Box
            sx={{
              display: 'inline-flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            <Typography
              style={{ fontWeight: '400', fontSize: '1.5rem' }}
              color="primary"
              variant="h1"
            >
              {t('NAVIGATION.REFERENCED_PAYMENTS_DETAIL')}
            </Typography>
            {reportDateLabel && (
              <Typography
                color="primary"
                variant="h3"
                style={{
                  fontWeight: '350',
                  marginLeft: '10px',
                  textAlign: 'end',
                  fontSize: '1.3rem'
                }}
              >
                {reportDateLabel}
              </Typography>
            )}
          </Box>
          <Box mt={3} height="70vh">
            {(() => {
              if (loading) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100vh',
                      flexDirection: 'column',
                      backgroundColor: 'white'
                    }}
                  >
                    <Loading label={t('COMMON.LOADING')} />
                  </Box>
                );
              }

              if (
                (success && !loading && !paymentDetails) ||
                paymentDetails.length === 0
              ) {
                return (
                  <NotFoundRecords
                    title="COMMON.NO_RECORDS_FOUND"
                    description="SANTANDER_REPORTS.NO_PAYMENT_DETAILS_FOUND"
                  />
                );
              }
              return (
                <SantanderPaymentDetailsTable
                  paymentDetails={paymentDetails}
                  count={paymentDetailsCount}
                  page={0}
                  handleChangePage={handleChangePage}
                  loading={loading}
                  success={success}
                  reportId={reportId}
                />
              );
            })()}
          </Box>
        </Container>
      </FadeIn>
    </Page>
  );
};

export default SantanderReportDetail;
